<mat-card class="rounded-2xl border border-gray-6 shadow-sm overflow-hidden">
  <!-- Card Header -->
  <mat-card-header class="bg-gray-1 py-4 px-6 border-b border-gray-6 flex flex-row items-center w-full">
    <div class="flex items-center gap-3">
      <div class="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-3 text-indigo-11">
        <mat-icon svgIcon="file-text" />
      </div>
      <div>
        <mat-card-title class="text-lg! font-bold! m-0! text-gray-12!">
          Documents & Justificatifs du bien
        </mat-card-title>
        <p class="text-xs text-gray-10 m-0 mt-0.5">
          Gérez les pièces justificatives et documents légaux rattachés à votre bien.
        </p>
      </div>
    </div>
  </mat-card-header>

  <mat-card-content class="p-6 flex flex-col gap-6">
    <!-- SECTION 1: TITRE DE PROPRIÉTÉ / TAXE FONCIÈRE (ANTI-FAKE & CERTIFICATION) -->
    <div class="rounded-2xl border border-gray-6 bg-gray-1/60 p-5 md:p-6 flex flex-col gap-4">
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div class="flex items-center gap-3">
          <div class="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-a3 text-indigo-11">
            <mat-icon svgIcon="shield-check" />
          </div>
          <div>
            <h4 class="text-sm font-semibold text-gray-12 m-0">Justificatif de propriété</h4>
            <p class="text-xs text-gray-10 m-0">Avis de taxe foncière ou attestation notariée de propriété</p>
          </div>
        </div>

        <!-- Verification Status Badge -->
        <div>
          @switch (verificationStatus()) {
            @case (PropertyVerificationStatus.VERIFIED) {
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-jade-a3 text-jade-11 border border-jade-a6 font-semibold text-xs"
              >
                <mat-icon class="text-jade-9 text-base w-4 h-4" svgIcon="circle-check" />
                Bien Certifié
              </span>
            }
            @case (PropertyVerificationStatus.REJECTED) {
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-ruby-a3 text-ruby-11 border border-ruby-a6 font-semibold text-xs"
              >
                <mat-icon class="text-ruby-9 text-base w-4 h-4" svgIcon="circle-alert" />
                Justificatif rejeté
              </span>
            }
            @case (PropertyVerificationStatus.FLAGGED) {
              <span
                class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-a3 text-amber-11 border border-amber-a6 font-semibold text-xs"
              >
                <mat-icon class="text-amber-9 text-base w-4 h-4" svgIcon="triangle-alert" />
                Signalé
              </span>
            }
            @default {
              @if (isOwnershipPendingReview()) {
                <span
                  class="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-a3 text-blue-11 border border-blue-a6 font-semibold text-xs"
                >
                  <mat-icon class="text-blue-9 text-base w-4 h-4" svgIcon="clock" />
                  En attente de validation
                </span>
              }
            }
          }
        </div>
      </div>

      <!-- Explanatory note depending on status -->
      @switch (verificationStatus()) {
        @case (PropertyVerificationStatus.VERIFIED) {
          <div
            class="flex items-start gap-2.5 rounded-xl bg-jade-2/50 border border-jade-5 p-3 text-xs text-jade-11"
          >
            <mat-icon class="text-jade-9 shrink-0 text-base w-4 h-4 mt-0.5" svgIcon="shield-check" />
            <span> Félicitations ! Votre titre de propriété a été validé. </span>
          </div>
        }
        @case (PropertyVerificationStatus.REJECTED) {
          <div
            class="flex items-start gap-2.5 rounded-xl bg-ruby-2/50 border border-ruby-5 p-3.5 text-xs text-ruby-11"
          >
            <mat-icon class="text-ruby-9 shrink-0 text-base w-4 h-4 mt-0.5" svgIcon="triangle-alert" />
            <div class="flex flex-col gap-1.5 flex-1">
              <span>Le document transmis n'a pas pu être validé.</span>
              @if (
                latestOwnershipDoc()?.rejectionReason || property().verification?.ownerRejectionReason;
                as reason
              ) {
                <div class="p-2 rounded-lg bg-ruby-3 border border-ruby-5 text-ruby-12 font-medium">
                  <strong>Motif du refus :</strong> {{ reason }}
                </div>
              }
              <span
                >Veuillez téléverser un nouvel avis de taxe foncière ou attestation notariée de propriété
                conforme.</span
              >
            </div>
          </div>
        }
        @default {
          @if (isOwnershipPendingReview()) {
            <div
              class="flex items-start gap-2.5 rounded-xl bg-blue-2/60 border border-blue-5 p-3 text-xs text-blue-11"
            >
              <mat-icon class="text-blue-9 shrink-0 text-base w-4 h-4 mt-0.5" svgIcon="clock" />
              <span>
                <strong>Vérification en cours :</strong> Votre justificatif de propriété a bien été déposé et
                est en cours d'examen par nos équipes. Dès validation, votre bien sera certifié et éligible à
                la publication.
              </span>
            </div>
          } @else {
            <div
              class="flex items-start gap-2.5 rounded-xl bg-amber-2/60 border border-amber-5 p-3 text-xs text-amber-11"
            >
              <mat-icon class="text-amber-9 shrink-0 text-base w-4 h-4 mt-0.5" svgIcon="triangle-alert" />
              <span>
                <strong>Publication requise :</strong> Sans justificatif de propriété valide (avis de taxe
                foncière ou attestation notariée), votre bien
                <strong>ne peut pas être publié publiquement</strong> ni recevoir de candidatures d'agents.
              </span>
            </div>
          }
        }
      }

      <!-- Ownership Documents Display -->
      @if (latestOwnershipDoc(); as doc) {
        <div class="flex flex-col gap-3">
          @if (previousOwnershipDocs().length > 0) {
            <div class="flex items-center justify-between">
              <span class="text-xs font-semibold text-gray-11"> Dernier document transmis </span>
            </div>
          }

          <etxe-property-document-item
            [doc]="doc"
            [propertyId]="property()._id"
            [editable]="editable()"
            [isAdmin]="isAdmin()"
            [isDeleting]="isDeletingId() === getFileId(doc)?.toString()"
            [canReplace]="!isDocumentVerified(doc)"
            (replace)="ownershipFileInput.click()"
            (delete)="deleteDocument($event)"
            (propertyUpdated)="propertyUpdated.emit($event)"
          />

          <!-- Previous Ownership Documents History (Collapsible drawer) -->
          @if (previousOwnershipDocs().length > 0) {
            <div class="mt-2 pt-3 border-t border-gray-5 flex flex-col gap-2.5">
              <button
                type="button"
                (click)="isHistoryExpanded.set(!isHistoryExpanded())"
                class="flex items-center justify-between w-full py-2 px-3 rounded-xl bg-gray-2/60 hover:bg-gray-3 border border-gray-5 transition-all text-xs font-semibold text-gray-12 group cursor-pointer"
              >
                <div class="flex items-center gap-2">
                  <div
                    class="flex h-6 w-6 items-center justify-center rounded-lg bg-gray-4 text-gray-11 group-hover:text-gray-12"
                  >
                    <mat-icon class="text-xs! w-3.5! h-3.5!" svgIcon="history" />
                  </div>
                  <span>Historique des dépôts précédents ({{ previousOwnershipDocs().length }})</span>
                </div>
                <div
                  class="flex items-center gap-1.5 text-[11px] font-medium text-gray-10 group-hover:text-gray-12"
                >
                  <span>{{ isHistoryExpanded() ? 'Masquer' : 'Afficher' }}</span>
                  <mat-icon
                    class="text-xs! w-3.5! h-3.5! transition-transform duration-200"
                    [class.rotate-180]="isHistoryExpanded()"
                    svgIcon="chevron-down"
                  />
                </div>
              </button>

              @if (isHistoryExpanded()) {
                <div class="flex flex-col gap-2 pt-1">
                  @for (oldDoc of previousOwnershipDocs(); track getFileId(oldDoc) || $index) {
                    <etxe-property-document-item
                      [doc]="oldDoc"
                      [propertyId]="property()._id"
                      [editable]="editable()"
                      [isAdmin]="isAdmin()"
                      [isDeleting]="isDeletingId() === getFileId(oldDoc)?.toString()"
                      [canReplace]="false"
                      [isHistory]="true"
                      (propertyUpdated)="propertyUpdated.emit($event)"
                    />
                  }
                </div>
              }
            </div>
          }
        </div>
      } @else if (editable()) {
        <!-- Upload Zone for Ownership -->
        <div
          class="flex flex-col items-center justify-center p-6 sm:p-8 rounded-xl border-2 border-dashed border-gray-6 surface-default text-center"
        >
          @if (isUploading() && uploadingType() === PropertyDocumentType.OWNERSHIP) {
            <mat-spinner diameter="32" class="mb-3" />
            <span class="text-sm font-medium text-gray-11">Téléversement du justificatif en cours...</span>
          } @else {
            <div
              class="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-3 text-indigo-11 mb-3"
            >
              <mat-icon svgIcon="upload" />
            </div>
            <div class="text-sm font-semibold text-gray-12">Déposez votre justificatif de propriété</div>
            <div class="text-xs text-gray-9 mt-1">
              Avis de taxe foncière, attestation notariée (PDF, JPG, PNG — max. 20 Mo)
            </div>
            <button mat-flat-button class="primary mt-4" type="button" (click)="ownershipFileInput.click()">
              <mat-icon svgIcon="plus" />
              <span>Choisir un fichier</span>
            </button>
          }
        </div>
      }

      <!-- Hidden file input for ownership -->
      <input
        #ownershipFileInput
        type="file"
        class="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp"
        aria-label="Sélectionner le titre de propriété"
        (change)="onFileSelected($event, PropertyDocumentType.OWNERSHIP)"
      />
    </div>

    <!-- SECTION 2: TOUS LES AUTRES DOCUMENTS DU BIEN -->
    <div class="flex flex-col gap-4">
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <h4 class="text-sm font-semibold text-gray-12 m-0">
            Autres pièces et documents ({{ otherDocuments().length }})
          </h4>
        </div>

        @if (editable()) {
          <button
            mat-flat-button
            class="primary text-xs"
            type="button"
            [matMenuTriggerFor]="docTypeMenu"
            [disabled]="isUploading() || isDeletingId() !== null"
          >
            <mat-icon svgIcon="plus" />
            <span>Ajouter un document</span>
          </button>

          <mat-menu #docTypeMenu="matMenu">
            <button mat-menu-item (click)="triggerGenericUpload(PropertyDocumentType.DPE)">
              <mat-icon svgIcon="gauge" />
              <span>Diagnostic DPE / DDT</span>
            </button>
            <button mat-menu-item (click)="triggerGenericUpload(PropertyDocumentType.CO_OWNERSHIP)">
              <mat-icon svgIcon="building" />
              <span>Copropriété / PV d'AG</span>
            </button>
            <button mat-menu-item (click)="triggerGenericUpload(PropertyDocumentType.FLOOR_PLAN)">
              <mat-icon svgIcon="map" />
              <span>Plan du bien</span>
            </button>
            <button mat-menu-item (click)="triggerGenericUpload(PropertyDocumentType.OTHER)">
              <mat-icon svgIcon="file-text" />
              <span>Autre document</span>
            </button>
          </mat-menu>
        }
      </div>

      <!-- Other Documents List -->
      @if (otherDocuments().length > 0) {
        <div class="flex flex-col gap-3">
          @for (doc of otherDocuments(); track getFileId(doc)) {
            <etxe-property-document-item
              [doc]="doc"
              [propertyId]="property()._id"
              [editable]="editable()"
              [isAdmin]="isAdmin()"
              [isDeleting]="isDeletingId() === getFileId(doc)?.toString()"
              (delete)="deleteDocument($event)"
              (propertyUpdated)="propertyUpdated.emit($event)"
            />
          }
        </div>
      } @else {
        <div
          class="flex items-center justify-between p-4 rounded-xl border border-gray-5 bg-gray-1/40 text-xs text-gray-10"
        >
          <span>Aucun document complémentaire ajouté (diagnostics DPE, plans, copropriété...).</span>
        </div>
      }

      <!-- Generic file input for other document types -->
      <input
        #genericFileInput
        type="file"
        class="hidden"
        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx"
        aria-label="Sélectionner un document complémentaire"
        (change)="onGenericFileSelected($event)"
      />
    </div>
  </mat-card-content>
</mat-card>
